import { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faVideo } from "@fortawesome/free-solid-svg-icons";
import TrashIcon from "../../icons/TrashIcon";
import ScheduleIcon from "@mui/icons-material/Schedule";
import AddIcon from "@mui/icons-material/Add";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  useAppointments,
  getAppointmentStatus,
  APPOINTMENT_STATUS,
  AppointmentStatus,
  DEFAULT_DURATION_MINUTES
} from "../../hooks/useAppointments";
import { Appointment } from "../../state/appointments";
import { useUserState } from "../../state/user";
import {
  sendAppointmentInvite,
  sendAppointmentCancelNotice
} from "../../services/appointments";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import DurationField, {
  isDurationValid
} from "../../components/common/DurationField";
import { typography } from "../../utils/typography";
import { semanticColors } from "../../utils/colors";
import { outlinedFieldSx, warningOutlineSx } from "../../utils/fieldStyles";
import { nextFiveMinutes } from "../../utils/date";
import { extractEmails, isValidEmail } from "../../utils/email";

const inputSx = outlinedFieldSx;

const paperSx = { borderRadius: "16px", overflow: "hidden" };

const headerSx = {
  background: semanticColors.gradient.surface,
  px: 3,
  py: 2,
  display: "flex",
  alignItems: "center",
  gap: 1.5
};

const headerTitleBoxSx = { flex: 1, minWidth: 0 };

const headerSubtitleSx = {
  ...typography.body.s,
  color: "rgba(255,255,255,0.75)"
};

const headerNameSx = {
  ...typography.body.lBold,
  color: "#fff",
  textTransform: "uppercase",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
};

const closeBtnSx = { color: "#fff", flexShrink: 0 };

const tabsWrapSx = {
  borderBottom: `1px solid ${semanticColors.border.light}`,
  bgcolor: semanticColors.surface.canvas
};

const tabsSx = {
  px: 2,
  minHeight: 44,
  "& .MuiTabs-indicator": {
    backgroundColor: semanticColors.border.focus,
    height: "2px"
  }
};

const tabSx = (active: boolean) => ({
  minHeight: 44,
  ...typography.body.m,
  fontWeight: active ? 600 : 400,
  color: active ? semanticColors.border.focus : semanticColors.text.primary,
  textTransform: "none" as const,
  "&.Mui-selected": { color: semanticColors.border.focus }
});

const dialogContentSx = { p: 0, bgcolor: semanticColors.surface.canvas };

const tabPanelSx = { p: 3 };

const emptyTextSx = {
  ...typography.body.l,
  color: semanticColors.text.secondary,
  textAlign: "center",
  py: 4
};

const upcomingListSx = { display: "flex", flexDirection: "column", gap: 1.5 };

const upcomingRowSx = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  p: "12px 16px",
  borderRadius: "12px",
  border: `1px solid ${semanticColors.border.light}`,
  bgcolor: semanticColors.surface.canvas
};

const upcomingIconSx = {
  color: semanticColors.border.focus,
  fontSize: 20,
  flexShrink: 0
};

const rowMainSx = { flex: 1, minWidth: 0 };

const upcomingDateSx = {
  ...typography.body.lBold,
  color: semanticColors.text.primary
};

const upcomingTimeSx = {
  ...typography.body.m,
  color: semanticColors.text.secondary
};

const startBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  background: semanticColors.gradient.surface,
  borderRadius: "40px",
  height: "34px",
  border: "none",
  cursor: "pointer",
  px: "14px",
  flexShrink: 0
};

const startLabelSx = { ...typography.body.mBold, color: "#fff" };

const cancelBtnSx = { color: semanticColors.status.error, flexShrink: 0 };

const historyListSx = { display: "flex", flexDirection: "column", gap: 0 };

const historyRowSx = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  py: "12px",
  px: "4px"
};

const historyIconSx = {
  color: semanticColors.text.secondary,
  fontSize: 18,
  flexShrink: 0
};

const historyMainSx = { flex: 1 };

const historyDateSx = {
  ...typography.body.l,
  color: semanticColors.text.primary
};

const historyStatusColor: Record<AppointmentStatus, string> = {
  [APPOINTMENT_STATUS.Scheduled]: semanticColors.text.secondary,
  [APPOINTMENT_STATUS.Done]: semanticColors.text.secondary,
  [APPOINTMENT_STATUS.Cancelled]: semanticColors.status.error,
  [APPOINTMENT_STATUS.Lapsed]: semanticColors.status.inProgress
};

const historyStatusLabelKey = {
  [APPOINTMENT_STATUS.Scheduled]: "videoDialog.consultationScheduled",
  [APPOINTMENT_STATUS.Done]: "videoDialog.consultationDone",
  [APPOINTMENT_STATUS.Cancelled]: "videoDialog.consultationCancelled",
  [APPOINTMENT_STATUS.Lapsed]: "videoDialog.consultationLapsed"
} as const;

const historyStatusSx = (status: AppointmentStatus) => ({
  ...typography.body.s,
  color: historyStatusColor[status]
});

const scheduleTabSx = {
  p: 3,
  display: "flex",
  flexDirection: "column",
  gap: 3
};

const patientInfoBoxSx = {
  p: "14px 16px",
  borderRadius: "8px",
  border: `1px solid ${semanticColors.border.light}`,
  bgcolor: semanticColors.surface.container
};

const patientInfoLabelSx = {
  ...typography.body.s,
  color: semanticColors.text.secondary,
  mb: 0.5
};

const patientInfoNameSx = {
  ...typography.body.lBold,
  color: semanticColors.text.primary,
  textTransform: "uppercase"
};

const notesFieldSx = {
  ...inputSx,
  "& .MuiOutlinedInput-root": {
    ...(inputSx["& .MuiOutlinedInput-root"] as object),
    height: "auto"
  }
};

const goEmailBtnSx = (disabled: boolean) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  background: disabled
    ? semanticColors.surface.container
    : semanticColors.gradient.surface,
  borderRadius: "40px",
  height: "48px",
  width: "100%",
  border: "none",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.6 : 1
});

const whiteIconSx = { fontSize: 18, color: "#fff" };

const whiteBoldLabelSx = { ...typography.body.lBold, color: "#fff" };

const emailStepTitleSx = {
  ...typography.body.lBold,
  color: semanticColors.text.primary
};

const emailStepSubtitleSx = {
  ...typography.body.s,
  color: semanticColors.text.secondary
};

const summaryBoxSx = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  p: "12px 16px",
  borderRadius: "10px",
  bgcolor: `${semanticColors.action.primary}14`,
  border: `1px solid ${semanticColors.action.primary}40`
};

const summaryIconSx = {
  color: semanticColors.action.primary,
  fontSize: 20,
  flexShrink: 0
};

const summaryDateSx = {
  ...typography.body.mBold,
  color: semanticColors.text.primary
};

const listLabelSx = {
  ...typography.body.mBold,
  color: semanticColors.text.secondary,
  mb: 1.5
};

const emailListSx = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  mb: 2
};

const emailRowSx = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  px: "14px",
  py: "10px",
  borderRadius: "10px",
  border: `1px solid ${semanticColors.border.light}`,
  bgcolor: semanticColors.surface.canvas
};

const emailIconSx = {
  fontSize: 18,
  color: semanticColors.action.primary,
  flexShrink: 0
};

const emailTextSx = {
  ...typography.body.l,
  color: semanticColors.text.primary,
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
};

const removeBtnSx = { color: semanticColors.feedback.danger, flexShrink: 0 };

const inputRowSx = { display: "flex", gap: 1 };

const emailInputSx = (hasError: boolean) => ({
  flex: 1,
  height: "44px",
  px: "14px",
  borderRadius: "8px",
  border: `1.5px solid ${
    hasError ? semanticColors.feedback.danger : semanticColors.border.strong
  }`,
  outline: "none",
  ...typography.body.l,
  color: semanticColors.text.primary,
  bgcolor: semanticColors.surface.canvas,
  "&:focus": {
    borderColor: hasError
      ? semanticColors.feedback.danger
      : semanticColors.border.focus
  },
  "&::placeholder": { color: semanticColors.text.subtle }
});

const addBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "4px",
  height: "44px",
  px: "14px",
  borderRadius: "8px",
  border: `1.5px solid ${semanticColors.action.primary}`,
  bgcolor: "transparent",
  cursor: "pointer",
  color: semanticColors.action.primary,
  flexShrink: 0,
  "&:hover": { bgcolor: `${semanticColors.action.primary}10` }
};

const addLabelSx = { ...typography.body.mBold, color: "inherit" };

const errorTextSx = {
  ...typography.body.s,
  color: semanticColors.feedback.danger,
  mt: "6px"
};

const feedbackBoxSx = (type: "success" | "error") => ({
  p: "12px 16px",
  borderRadius: "10px",
  bgcolor:
    type === "success"
      ? `${semanticColors.feedback.ok}18`
      : `${semanticColors.feedback.danger}18`,
  border: `1px solid ${
    type === "success"
      ? semanticColors.feedback.ok
      : semanticColors.feedback.danger
  }50`
});

const feedbackTextSx = (type: "success" | "error") => ({
  ...typography.body.m,
  color:
    type === "success"
      ? semanticColors.feedback.ok
      : semanticColors.feedback.danger
});

const actionsRowSx = { display: "flex", gap: 1.5 };

const backBtnSx = {
  flex: 1,
  height: "48px",
  borderRadius: "40px",
  border: `1.5px solid ${semanticColors.border.strong}`,
  bgcolor: "transparent",
  cursor: "pointer",
  ...typography.body.lBold,
  color: semanticColors.text.primary,
  "&:hover": { bgcolor: semanticColors.surface.containerLow }
};

const confirmBtnSx = (disabled: boolean) => ({
  flex: 2,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  height: "48px",
  borderRadius: "40px",
  border: "none",
  background: semanticColors.gradient.surface,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.7 : 1
});

interface Props {
  patientName: string;
  patient?: any;
  open: boolean;
  onClose: () => void;
}

export default function PatientVideoDialog({
  patientName,
  patient,
  open,
  onClose
}: Props) {
  const { t } = useTranslation();
  const { token, user } = useUserState();
  const [tab, setTab] = useState(0);
  const [
    scheduledDateTime,
    setScheduledDateTime
  ] = useState<moment.Moment | null>(() => nextFiveMinutes());
  // Raw text so the field can be cleared while typing (see DurationField).
  const [duration, setDuration] = useState(String(DEFAULT_DURATION_MINUTES));
  const [notes, setNotes] = useState("");

  // cancellation confirmation
  const [appointmentToCancel, setAppointmentToCancel] = useState<string | null>(
    null
  );
  const [cancelling, setCancelling] = useState(false);

  // email step state
  const [emailStep, setEmailStep] = useState(false);
  const [emailList, setEmailList] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    msg: string;
  } | null>(null);

  // Appointments are identified by the patient's clinical file. The contact
  // record carries `clinicalFileId` (distinct from its own `_id`).
  const clinicalFileId: string | undefined =
    patient?.clinicalFileId ?? patient?.fileId ?? patient?.scriboPartition;

  const { upcoming, past, addAppointment, deleteAppointment } = useAppointments(
    clinicalFileId
  );
  const history = useHistory();

  const scheduleDisabled = !scheduledDateTime || !isDurationValid(duration);

  const resetScheduleStep = () => {
    setScheduledDateTime(nextFiveMinutes());
    setDuration(String(DEFAULT_DURATION_MINUTES));
    setNotes("");
    setEmailStep(false);
    setEmailList([]);
    setNewEmail("");
    setEmailError("");
    setFeedback(null);
  };

  // Start each opening from a clean schedule form. The dialog stays mounted
  // across open/close, and it can now be closed by the browser back button
  // (which bypasses handleClose), so resetting on open covers every close path.
  useEffect(() => {
    if (open) resetScheduleStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleConfirmCancel = async () => {
    if (!appointmentToCancel) return;
    // Capture the appointment before deleting — it leaves the lists afterwards.
    const appt = upcoming.find(a => a.id === appointmentToCancel);
    setCancelling(true);
    try {
      await deleteAppointment(appointmentToCancel);
      // Notify the patient that the appointment was cancelled (mirrors the
      // invite sent on scheduling). A notify failure must not block the cancel.
      const emails = extractEmails(patient);
      if (token && appt && emails.length > 0) {
        try {
          await sendAppointmentCancelNotice(token, {
            emails,
            patientName: appt.patientName,
            patientGender: patient?.gender,
            doctorName:
              (user as any)?.clinicalName ??
              (user as any)?.personalInfo?.givenName ??
              "",
            doctorGender: (user as any)?.personalInfo?.gender,
            // Same date format the (working) /notify uses — moment().format()
            // keeps the local offset (e.g. +01:00) rather than a UTC "Z".
            scheduledDate: moment(appt.start).format()
          });
        } catch {
          // cancellation already succeeded; ignore notify failure
        }
      }
      setAppointmentToCancel(null);
    } catch {
      // keep dialog open to allow retry
    } finally {
      setCancelling(false);
    }
  };

  const handleClose = () => {
    resetScheduleStep();
    onClose();
  };

  const handleTabChange = (_: any, v: number) => {
    setTab(v);
    resetScheduleStep();
  };

  // Step 1 → Step 2: validate date and open email step
  const handleGoToEmailStep = () => {
    if (!scheduledDateTime || !isDurationValid(duration)) return;
    setEmailList(extractEmails(patient));
    setEmailStep(true);
    setFeedback(null);
  };

  // Add email to list
  const handleAddEmail = () => {
    const val = newEmail.trim();
    // Empty is a no-op (the field may have already auto-committed on blur).
    if (!val) return;
    if (!isValidEmail(val)) {
      setEmailError(t("videoDialog.invalidEmail"));
      return;
    }
    if (emailList.includes(val)) {
      setNewEmail("");
      return;
    }
    setEmailList(prev => [...prev, val]);
    setNewEmail("");
    setEmailError("");
  };

  const handleNewEmailKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleRemoveEmail = (email: string) => {
    setEmailList(prev => prev.filter(e => e !== email));
  };

  // Step 2 confirm: save appointment + send emails
  const handleConfirm = async () => {
    // Accept an email typed but not yet added via the "Add" button, so
    // the common single-recipient case needs no extra click.
    const pending = newEmail.trim();
    let emailsToSend = emailList;
    if (pending) {
      if (!isValidEmail(pending)) {
        setEmailError(t("videoDialog.invalidEmail"));
        return;
      }
      emailsToSend = emailList.includes(pending)
        ? emailList
        : [...emailList, pending];
    }
    if (emailsToSend.length === 0) {
      setEmailError(t("videoDialog.noEmails"));
      return;
    }
    if (!scheduledDateTime || !isDurationValid(duration)) return;
    if (!clinicalFileId) {
      setFeedback({ type: "error", msg: t("videoDialog.noClinicalFile") });
      return;
    }

    setSending(true);
    try {
      await addAppointment({
        start: scheduledDateTime.toDate(),
        clinicalFileId,
        patientName,
        durationMinutes: parseInt(duration, 10),
        notes: notes.trim() || undefined
      });
    } catch {
      setFeedback({ type: "error", msg: t("videoDialog.scheduleError") });
      setSending(false);
      return;
    }

    try {
      await sendAppointmentInvite(token!, {
        emails: emailsToSend,
        patientName,
        patientGender: patient?.gender,
        doctorName:
          (user as any)?.clinicalName ??
          (user as any)?.personalInfo?.givenName ??
          "",
        doctorGender: (user as any)?.personalInfo?.gender,
        scheduledDate: scheduledDateTime.format()
      });
      setFeedback({ type: "success", msg: t("videoDialog.sendSuccess") });
    } catch {
      setFeedback({ type: "error", msg: t("videoDialog.sendError") });
    } finally {
      setSending(false);
    }

    // Go to scheduled tab after a short delay
    setTimeout(() => {
      resetScheduleStep();
      setTab(0);
    }, 1800);
  };

  const handleStart = (appt: Appointment) => {
    // Navigating to /video unmounts this page (and the modal); just clear the
    // local schedule state — no need to also pop the ?video=1 entry.
    resetScheduleStep();
    history.push("/video", {
      autoStart: true,
      patientName: appt.patientName,
      appointmentId: appt.id,
      // Carried through so the room can be linked to the appointment even
      // before the appointments list finishes loading on the /video screen.
      externalId: appt.externalId
    });
  };

  const displayName =
    patientName.length > 32 ? patientName.slice(0, 32) + "…" : patientName;

  const tabs = [
    t("videoDialog.tabScheduled", { count: upcoming.length }),
    t("videoDialog.tabHistory", { count: past.length }),
    t("videoDialog.tabScheduleNew")
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: paperSx }}
      >
        {/* Header */}
        <DialogTitle sx={headerSx}>
          <FontAwesomeIcon
            icon={faVideo}
            style={{ color: "#fff", fontSize: 20 }}
          />
          <Box sx={headerTitleBoxSx}>
            <Typography sx={headerSubtitleSx}>
              {t("videoDialog.title")}
            </Typography>
            <Typography sx={headerNameSx}>{displayName}</Typography>
          </Box>
          <IconButton onClick={handleClose} sx={closeBtnSx}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Tabs */}
        <Box sx={tabsWrapSx}>
          <Tabs value={tab} onChange={handleTabChange} sx={tabsSx}>
            {tabs.map((label, i) => (
              <Tab key={i} label={label} sx={tabSx(tab === i)} />
            ))}
          </Tabs>
        </Box>

        <DialogContent sx={dialogContentSx}>
          {/* ── Tab 0: Scheduled ── */}
          {tab === 0 && (
            <Box sx={tabPanelSx}>
              {upcoming.length === 0 ? (
                <Typography sx={emptyTextSx}>
                  {t("videoDialog.noUpcoming")}
                </Typography>
              ) : (
                <Box sx={upcomingListSx}>
                  {upcoming.map(appt => (
                    <Box key={appt.id} sx={upcomingRowSx}>
                      <ScheduleIcon sx={upcomingIconSx} />
                      <Box sx={rowMainSx}>
                        <Typography sx={upcomingDateSx}>
                          {moment(appt.start).format("DD/MM/YYYY")}
                        </Typography>
                        <Typography sx={upcomingTimeSx}>
                          {moment(appt.start).format("HH:mm")} ·{" "}
                          {appt.durationMinutes} min
                        </Typography>
                      </Box>
                      <Box
                        component="button"
                        onClick={() => handleStart(appt)}
                        sx={startBtnSx}
                      >
                        <FontAwesomeIcon
                          icon={faVideo}
                          style={{ fontSize: 13, color: "#fff" }}
                        />
                        <Typography sx={startLabelSx}>
                          {t("videoDialog.start")}
                        </Typography>
                      </Box>
                      <Tooltip title={t("videoDialog.deleteTooltip")}>
                        <IconButton
                          size="small"
                          onClick={() => setAppointmentToCancel(appt.id)}
                          sx={cancelBtnSx}
                        >
                          <TrashIcon size={16} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* ── Tab 1: History ── */}
          {tab === 1 && (
            <Box sx={tabPanelSx}>
              {past.length === 0 ? (
                <Typography sx={emptyTextSx}>
                  {t("videoDialog.noHistory")}
                </Typography>
              ) : (
                <Box sx={historyListSx}>
                  {past.map((appt, i) => (
                    <Box key={appt.id}>
                      <Box sx={historyRowSx}>
                        <ScheduleIcon sx={historyIconSx} />
                        <Box sx={historyMainSx}>
                          <Typography sx={historyDateSx}>
                            {moment(appt.start).format(
                              t("common.datetimeFormat")
                            )}
                          </Typography>
                          <Typography
                            sx={historyStatusSx(getAppointmentStatus(appt))}
                          >
                            {t(
                              historyStatusLabelKey[getAppointmentStatus(appt)]
                            )}
                          </Typography>
                        </Box>
                      </Box>
                      {i < past.length - 1 && <Divider />}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* ── Tab 2: Schedule New ── */}
          {tab === 2 && !emailStep && (
            <Box sx={scheduleTabSx}>
              {/* Patient info */}
              <Box sx={patientInfoBoxSx}>
                <Typography sx={patientInfoLabelSx}>
                  {t("videoDialog.patient")}
                </Typography>
                <Typography sx={patientInfoNameSx}>{patientName}</Typography>
              </Box>

              <DateTimePicker
                label={t("videoDialog.dateTimeLabel")}
                value={scheduledDateTime}
                onChange={val => setScheduledDateTime(val)}
                disablePast
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: { ...inputSx, ...warningOutlineSx }
                  }
                }}
              />

              <DurationField
                value={duration}
                onChange={setDuration}
                sx={inputSx}
              />

              <TextField
                label={t("videoConsultations.notesLabel")}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                sx={notesFieldSx}
              />

              <Box
                component="button"
                onClick={handleGoToEmailStep}
                disabled={scheduleDisabled}
                sx={goEmailBtnSx(scheduleDisabled)}
              >
                <ScheduleIcon sx={whiteIconSx} />
                <Typography sx={whiteBoldLabelSx}>
                  {t("videoDialog.scheduleButton")}
                </Typography>
              </Box>
            </Box>
          )}

          {/* ── Tab 2: Email step ── */}
          {tab === 2 && emailStep && (
            <Box sx={scheduleTabSx}>
              {/* Title */}
              <Box>
                <Typography sx={emailStepTitleSx}>
                  {t("videoDialog.emailStepTitle")}
                </Typography>
                <Typography sx={emailStepSubtitleSx}>
                  {t("videoDialog.emailStepSubtitle")}
                </Typography>
              </Box>

              {/* Appointment summary */}
              <Box sx={summaryBoxSx}>
                <ScheduleIcon sx={summaryIconSx} />
                <Box>
                  <Typography sx={summaryDateSx}>
                    {moment(scheduledDateTime).format("DD/MM/YYYY")}
                  </Typography>
                  <Typography sx={emailStepSubtitleSx}>
                    {moment(scheduledDateTime).format("HH:mm")} · {patientName}
                  </Typography>
                </Box>
              </Box>

              {/* Email list */}
              <Box>
                <Typography sx={listLabelSx}>
                  {t("videoDialog.emailListLabel")}
                </Typography>

                {emailList.length > 0 && (
                  <Box sx={emailListSx}>
                    {emailList.map(email => (
                      <Box key={email} sx={emailRowSx}>
                        <EmailOutlinedIcon sx={emailIconSx} />
                        <Typography sx={emailTextSx}>{email}</Typography>
                        <Tooltip title={t("videoDialog.removeEmail")}>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveEmail(email)}
                            sx={removeBtnSx}
                          >
                            <TrashIcon size={16} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Add email input */}
                <Box sx={inputRowSx}>
                  <Box
                    component="input"
                    type="email"
                    value={newEmail}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setNewEmail(e.target.value);
                      setEmailError("");
                    }}
                    onKeyDown={handleNewEmailKeyDown}
                    onBlur={() => {
                      // A valid email commits itself to the list above, freeing
                      // the field for the next — no "Add" click needed.
                      if (isValidEmail(newEmail)) handleAddEmail();
                    }}
                    placeholder={
                      t("videoDialog.emailInputPlaceholder") as string
                    }
                    sx={emailInputSx(!!emailError)}
                  />
                  {/* First email is a plain fill-in that auto-commits to the
                      list above once valid. The "Add" button only shows
                      from the second email onwards (list already has entries). */}
                  {emailList.length > 0 && (
                    <Box
                      component="button"
                      onClick={handleAddEmail}
                      sx={addBtnSx}
                    >
                      <AddIcon sx={{ fontSize: 18 }} />
                      <Typography sx={addLabelSx}>
                        {t("videoDialog.addEmail")}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {emailError && (
                  <Typography sx={errorTextSx}>{emailError}</Typography>
                )}
              </Box>

              {/* Feedback */}
              {feedback && (
                <Box sx={feedbackBoxSx(feedback.type)}>
                  <Typography sx={feedbackTextSx(feedback.type)}>
                    {feedback.msg}
                  </Typography>
                </Box>
              )}

              {/* Actions */}
              <Box sx={actionsRowSx}>
                <Box
                  component="button"
                  onClick={() => {
                    setEmailStep(false);
                    setEmailError("");
                    setFeedback(null);
                  }}
                  sx={backBtnSx}
                >
                  {t("videoDialog.back")}
                </Box>
                <Box
                  component="button"
                  onClick={handleConfirm}
                  disabled={sending || !!feedback}
                  sx={confirmBtnSx(sending || !!feedback)}
                >
                  {sending ? (
                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                  ) : (
                    <EmailOutlinedIcon sx={whiteIconSx} />
                  )}
                  <Typography sx={whiteBoldLabelSx}>
                    {sending
                      ? t("videoDialog.sending")
                      : t("videoDialog.confirmAndSend")}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!appointmentToCancel}
        destructive
        loading={cancelling}
        title={t("videoConsultations.cancelTitle")}
        message={t("videoConsultations.cancelMessage")}
        confirmLabel={t("videoConsultations.cancelConfirm")}
        cancelLabel={t("videoConsultations.cancelDismiss")}
        onConfirm={handleConfirmCancel}
        onClose={() => !cancelling && setAppointmentToCancel(null)}
      />
    </>
  );
}
